import * as React from 'react';
import styled from 'styled-components';
import { colorsV2 } from 'style/colors-v2';
import { Typography } from 'components/DesignSystemV2/Typography';
import {
  ReadingIcon,
  SpeakingIcon,
  WritingIcon,
  VocabGrammarIcon
} from './icons/SkillIcons';
import { CrossIcon, CheckIcon } from 'components/Icon/SVGIcons';
import { splitTitleAndDescription } from 'utils/stringUtils';
import { imageFitStyle } from 'style/css-style';
import { LazyImage } from 'components/LazyImage';
import { createDashedBorder } from 'utils/styleUtils';
import { toScreen } from 'utils/media-query/responsive.util';
import { desktopFirstV3 } from 'style/breakpointsv3';

const Main = styled.div`
  display: grid;
  gap: 24px;
  margin-top: 64px;
  border-top: 1px solid ${colorsV2.neutral20};
  padding-top: 64px;
`;

const Header = styled.div`
  display: grid;
  gap: 16px;
  max-width: 530px;
`;

const SkillList = styled.div`
  display: grid;
  gap: 12px;
`;

const SkillItemWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 312px;
  gap: 12px;

  ${toScreen(desktopFirstV3.md)} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const SkillContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 32px;
  row-gap: 20px;
  padding: 32px;
  border-radius: 20px;
  border: 1px solid ${colorsV2.neutral20};

  ${toScreen(desktopFirstV3.sm)} {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const SkillHeader = styled.div`
  display: flex;
  gap: 12px;
  grid-column: 1 / -1;
`;

const ProsConsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ProsConsItem = styled.div`
  display: flex;
  align-items: start;
  gap: 12px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const StudentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  border-radius: 20px;
  padding: 32px;
  position: relative;

  ${createDashedBorder({
    strokeWidth: 2,
    borderRadius: 20,
    stroke: colorsV2.neutral20
  })}

  ${toScreen(desktopFirstV3.md)} {
    flex-direction: row;
    align-items: end;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .pin-icon {
    position: absolute;
    top: 8px;
    left: 8px;

    ${toScreen(desktopFirstV3.md)} {
      left: initial;
      right: -2px;
    }
  }
`;

const Role = styled(Typography)`
  text-transform: uppercase;
`;

const StudentInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  ${toScreen(desktopFirstV3.md)} {
    align-items: start;
  }
`;

const StudentNameAvatar = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 16px;

  ${toScreen(desktopFirstV3.md)} {
    flex-direction: row;
  }
`;

const StudentNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;

  ${toScreen(desktopFirstV3.md)} {
    align-items: start;
    white-space: nowrap;
  }
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  box-shadow: 0px 24px 32px 0px #3042780f, 0px 16px 24px 0px #3042780f,
    0px 4px 8px 0px #3042780f;
  border: 2px solid ${colorsV2.white100};
  position: relative;
  overflow: hidden;

  img {
    ${imageFitStyle('cover')};
  }
`;

const Score = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 8px 16px;
  border-radius: 16px;
  border: 1px solid ${colorsV2.neutral20};
`;

const SkillIcon = ({ icon }) => {
  switch (icon) {
    case 'speak':
      return <SpeakingIcon />;
    case 'read':
      return <ReadingIcon />;
    case 'write':
      return <WritingIcon />;
    case 'vocab-grammar':
      return <VocabGrammarIcon />;
  }
};

const ProsCons = ({ data = [], icon }) => {
  return (
    <ProsConsList>
      {data.map((content, index) => {
        const titleDesc = splitTitleAndDescription(content);
        return (
          <ProsConsItem key={index}>
            <IconWrapper>{icon}</IconWrapper>
            <Typography variant="regular/16-24" color={colorsV2.neutral180}>
              {titleDesc.title && <strong>{titleDesc.title}: </strong>}
              {titleDesc.description}
            </Typography>
          </ProsConsItem>
        );
      })}
    </ProsConsList>
  );
};

const Student = ({ student }) => {
  const { fullName, role, school, score, skill, avatar } = student;
  return (
    <StudentWrapper>
      <LazyImage handle="cm9ggf21hu3w7071421cwiz8c" className="pin-icon" />
      <StudentInfo>
        <Role variant="semi-bold/14-20" color={colorsV2.neutral120}>
          {role}
        </Role>
        <StudentNameAvatar>
          <Avatar>
            <LazyImage handle={avatar} alt={fullName} />
          </Avatar>
          <StudentNameWrapper>
            <Typography variant="semi-bold/18-24" color={colorsV2.neutral180}>
              {fullName}
            </Typography>
            <Typography variant="regular/16-20" color={colorsV2.neutral120}>
              {school}
            </Typography>
          </StudentNameWrapper>
        </StudentNameAvatar>
      </StudentInfo>
      <Score>
        <Typography variant="bold/20-28" color={colorsV2.primary100} as="span">
          {score}
        </Typography>
        <Typography variant="semi-bold/14-20" color={colorsV2.neutral140}>
          {skill}
        </Typography>
      </Score>
    </StudentWrapper>
  );
};

const SkillItem = ({ skill }) => {
  const { name, icon, cons, pros, student } = skill;
  const iconProps = { withBackground: false, size: 24 };
  return (
    <SkillItemWrapper>
      <SkillContent>
        <SkillHeader>
          <SkillIcon icon={icon} />
          <Typography variant="semi-bold/20-28" color={colorsV2.neutral200}>
            {name}
          </Typography>
        </SkillHeader>
        <ProsCons
          data={cons}
          icon={<CrossIcon {...iconProps} iconColor={colorsV2.yellow120} />}
        />
        <ProsCons
          data={pros}
          icon={<CheckIcon {...iconProps} iconColor={colorsV2.green120} />}
        />
      </SkillContent>
      <Student student={student} />
    </SkillItemWrapper>
  );
};

const LinearthinkingApplyToSkills = ({ data }) => {
  const { title, description, skills } = data;

  return (
    <Main>
      <Header>
        <Typography variant="medium/16-24" color={colorsV2.gray120}>
          {description}
        </Typography>
        <Typography
          variant="bold/24-32"
          tabletVariant="bold/32-40"
          desktopVariant="bold/40-48"
          color={colorsV2.black100}
          v3
        >
          {title}
        </Typography>
      </Header>
      <SkillList>
        {skills.map((skill, index) => (
          <SkillItem key={index} skill={skill} />
        ))}
      </SkillList>
    </Main>
  );
};

export default LinearthinkingApplyToSkills;
